/**
 * Model Selector Component
 * Searchable dropdown for selecting AI models from OpenRouter
 */

import { useState, useEffect, useMemo } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { OpenRouterModel } from '@/types/ai';
import {
  fetchModels,
  createAiClient,
  getRecommendedModels,
  formatModelPrice,
} from '@/services/ai';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface ModelSelectorProps {
  apiKey: string;
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

export function ModelSelector({
  apiKey,
  value,
  onValueChange,
  disabled = false,
  className,
}: ModelSelectorProps) {
  const [open, setOpen] = useState(false);
  const [models, setModels] = useState<OpenRouterModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch models when API key is available
  useEffect(() => {
    if (!apiKey) {
      setModels([]);
      return;
    }

    let cancelled = false;

    async function loadModels() {
      setIsLoading(true);
      setError(null);

      try {
        const client = createAiClient(apiKey);
        const fetchedModels = await fetchModels(client);

        if (!cancelled) {
          setModels(fetchedModels);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load models');
          console.error('Error fetching models:', err);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadModels();

    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  // Group models: recommended first, then by provider
  const groupedModels = useMemo(() => {
    const recommended = getRecommendedModels();
    const recommendedModels: OpenRouterModel[] = [];
    const otherModels: OpenRouterModel[] = [];

    models.forEach((model) => {
      if (recommended.includes(model.id)) {
        recommendedModels.push(model);
      } else {
        otherModels.push(model);
      }
    });

    // Sort recommended by the order in the recommended list
    recommendedModels.sort(
      (a, b) => recommended.indexOf(a.id) - recommended.indexOf(b.id),
    );

    return { recommended: recommendedModels, other: otherModels };
  }, [models]);

  // Get the selected model's display name
  const selectedModel = models.find((m) => m.id === value);
  const displayValue = selectedModel?.name || value || 'Select a model...';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-controls="model-selector-listbox"
          disabled={disabled || !apiKey}
          className={cn('w-full justify-between', className)}
        >
          <span className="truncate">
            {isLoading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading models...
              </span>
            ) : (
              displayValue
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search models..." />
          <CommandList id="model-selector-listbox">
            <CommandEmpty>{error ? error : 'No models found.'}</CommandEmpty>

            {groupedModels.recommended.length > 0 && (
              <CommandGroup heading="Recommended">
                {groupedModels.recommended.map((model) => (
                  <ModelCommandItem
                    key={model.id}
                    model={model}
                    isSelected={value === model.id}
                    onSelect={() => {
                      onValueChange(model.id);
                      setOpen(false);
                    }}
                  />
                ))}
              </CommandGroup>
            )}

            {groupedModels.other.length > 0 && (
              <CommandGroup heading="All Models">
                {groupedModels.other.map((model) => (
                  <ModelCommandItem
                    key={model.id}
                    model={model}
                    isSelected={value === model.id}
                    onSelect={() => {
                      onValueChange(model.id);
                      setOpen(false);
                    }}
                  />
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface ModelCommandItemProps {
  model: OpenRouterModel;
  isSelected: boolean;
  onSelect: () => void;
}

function ModelCommandItem({
  model,
  isSelected,
  onSelect,
}: ModelCommandItemProps) {
  return (
    <CommandItem
      value={`${model.id} ${model.name}`}
      onSelect={onSelect}
      className="flex items-center justify-between"
    >
      <div className="flex items-center gap-2">
        <Check
          className={cn('h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')}
        />
        <div className="flex flex-col">
          <span className="font-medium">{model.name}</span>
          <span className="text-xs text-muted-foreground">{model.id}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>{formatModelPrice(model.pricing.prompt)}</span>
        <span className="text-muted-foreground/50">|</span>
        <span>{Math.round(model.context_length / 1000)}k ctx</span>
      </div>
    </CommandItem>
  );
}
